import { memo } from '@tanstack/react-table';
import {
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  useMemo,
  type MouseEvent as ReactMouseEvent,
  Children,
} from 'react';
import { isElement } from 'react-is';

import {
  type Cell,
  type Column,
  type InitialTableState,
  type Row,
  type RowData,
  type Table,
  type TableFeature,
  type TableOptions,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import {
  type DataTableV2ColumnDef,
  type DataTableV2RowData,
} from '../../public.api.js';

/**
 * Props for slotting selector component `DataTableV2ColumnActions`.
 * @public
 */
export interface DataTableV2ColumnActionsProps<
  TData extends DataTableV2RowData,
> {
  children:
    | ReactNode
    | ((
        column: DataTableV2ColumnDef<TData>,
      ) => ReactElement<PropsWithChildren>);
  /**
   * Column id that the given columnactions apply to. If left empty, the given cell actions
   * apply to all columns that allow cell actions.
   */
  column?: string;
}

/**
 * Slotting selector component for the `DataTableV2ColumnActions`.
 * @public
 */
export function DataTableV2ColumnActions<TData extends DataTableV2RowData>(
  props: DataTableV2ColumnActionsProps<TData>,
) {
  return null;
}

/**
 * Typeguard function that will check if a ReactNode is a DataTable.ColumnActions
 * @internal
 */
function isDataTableV2ColumnActions<TData extends DataTableV2RowData>(
  child: ReactNode,
): child is ReactElement<DataTableV2ColumnActionsProps<TData>> {
  if (isElement(child) && child.type === DataTableV2ColumnActions) {
    return true;
  }
  return false;
}

/**
 * Props for slotting selector component `DataTableV2CellActions`.
 * @public
 */
export interface DataTableV2CellActionsProps<TData extends DataTableV2RowData> {
  children:
    | ReactNode
    | ((props: {
        columnDef: DataTableV2ColumnDef<TData>;
        cellValue: unknown;
        row: TData;
        rowId: string;
      }) => ReactElement<PropsWithChildren>);
  /**
   * Column id that the given cellactions apply to. If left empty, the given cell actions
   * apply to all columns that allow cell actions.
   */
  column?: string;
}

/**
 * Slotting selector component for the `DataTableV2CellActions`.
 * @public
 */
export function DataTableV2CellActions<TData extends DataTableV2RowData>(
  props: DataTableV2CellActionsProps<TData>,
) {
  return null;
}

/**
 * Typeguard function that will check if a ReactNode is a DataTable.CellActions
 * @internal
 */
function _isDataTableV2CellActions<TData extends DataTableV2RowData>(
  child: ReactNode,
): child is ReactElement<DataTableV2CellActionsProps<TData>> {
  if (isElement(child) && child.type === DataTableV2CellActions) {
    return true;
  }
  return false;
}

/**
 * Props for slotting selector component `DataTableV2RowActions`.
 * @public
 */
export interface DataTableV2RowActionsProps<TData extends DataTableV2RowData> {
  children: (
    row: TData,
    {
      rowDensity,
      rowIndex,
      rowId,
    }: {
      rowDensity: 'default' | 'condensed' | 'comfortable';
      rowIndex: number;
      rowId: string;
    },
  ) => ReactElement<PropsWithChildren>;
}

/**
 * Slotting selector component for the `DataTableV2RowActions`.
 * @public
 */
export function DataTableV2RowActions<TData extends DataTableV2RowData>(
  props: DataTableV2RowActionsProps<TData>,
) {
  return null;
}

/**
 * Typeguard function that will check if a ReactNode is a DataTable.RowActions
 * @internal
 */
function isDataTableV2RowActions<TData extends DataTableV2RowData>(
  child: ReactNode,
): child is ReactElement<DataTableV2RowActionsProps<TData>> {
  if (isElement(child) && child.type === DataTableV2RowActions) {
    return true;
  }
  return false;
}

//#endregion public component configurations

//#region React table extension interfaces

/**
 * Extension interface to extend the TableState of the tanstack table.
 * @internal
 */
export interface DataTableV2UserActionsState {
  floatingMenuInfo?: {
    type: 'headerAction' | 'cellAction' | null;
    columnId: string | null;
    rowId: string | null;
    x: number;
    y: number;
  };
}

/**
 * Extension interface to extend the useTable hook options with the UserActions options.
 * @internal
 */
export interface DataTableV2UserActionsOptions<
  TData extends DataTableV2RowData,
> {
  /**
   * Enables columnActions as a feature on the table.
   */
  enableColumnActions?: boolean;

  /** Holds a collection of all cell actions to render when necessary. */
  cellActions?: {
    [key in symbol | string]: Array<
      | ReactNode
      | ((props: {
          columnDef: DataTableV2ColumnDef<TData>;
          cellValue: unknown;
          row: TData;
          rowId: string;
        }) => ReactElement<PropsWithChildren>)
    >;
  };

  /**
   * Enables cellActions as a feature on the table.
   */
  enableCellActions?: boolean;

  builtinColumnActions?: Record<
    string | symbol,
    (column: Column<TData>) => ReactElement<PropsWithChildren> | null
  >;

  /** Holds a collection of all column actions to render when necessary. */
  columnActions?: {
    [key in symbol | string]: Array<
      | ReactNode
      | ((
          column: DataTableV2ColumnDef<TData>,
        ) => ReactElement<PropsWithChildren>)
    >;
  };

  /**
   * Enables rowActions as a feature on the table.
   */
  enableRowActions?: boolean;

  /** Holds a collection of all row actions to render when necessary. */
  rowActions?: (
    row: TData,
    {
      rowDensity,
      rowIndex,
      rowId,
    }: {
      rowDensity: 'default' | 'condensed' | 'comfortable';
      rowIndex: number;
      rowId: string;
    },
  ) => ReactNode;

  /**
   * Reference map to get to the original (non-exteded version) of the column
   * definition passed by the consumer in order to get emit it with a column or cell action.
   */
  consumerColumnDefMap?: Map<string, DataTableV2ColumnDef<TData>>;
}

/** @internal */
export interface DataTableV2CellOrColumnActions {
  builtinActions?: ReactNode[] | null;
  customActions?: ReactNode[] | null;
}

/** Extension interface to extend the types for the `Table` from the tanstack table */
export interface DataTableV2UserActionsInstance {
  /**
   * @internal
   * Allows to set the state of the floating menu and the context of the floating menu
   */
  _setFloatingMenuInfo: (
    columnId: string,
    rowId: string | null,
    x: number,
    y: number,
  ) => void;

  /**
   * Allows global access to the column actions by columnId.
   */
  getUserActionsForColumn: (columnId: string) => DataTableV2CellOrColumnActions;

  /**
   * Allows global access to the cell actions by columnId and rowId identifier.
   */
  getUserActionsForCell: (
    columnId: string,
    rowId: string,
  ) => DataTableV2CellOrColumnActions;

  /**
   * Access to the floating context menu handler
   */
  getUserActionFloatingMenuContextMenuHandler: () => (
    event: ReactMouseEvent<HTMLDivElement>,
  ) => void;
}

/**
 * Extension interface to extend the types of the `Column` from the tanstack table.
 * @internal
 */
export interface DataTableV2UserActionsColumn {
  /**
   * Defines if the header does have user actions defined
   */
  getCanUserActions: () => boolean;

  /**
   * Returns the defined user actions that can be passed to the menu content.
   */
  getUserActions: () => DataTableV2CellOrColumnActions;
}

/**
 * Extension interface to extend the types of the `Cell` from the tanstack table.
 * @internal
 */
export interface DataTableV2UserActionsCell {
  /**
   * Defines if the header does have user actions defined
   */
  getCanUserActions: () => boolean;

  /**
   * Returns the defined user actions that can be passed to the menu content.
   */
  getUserActions: () => DataTableV2CellOrColumnActions;
}

/**
 * Extension interface to extend the types of the `ColumnDef` from the tanstack table.
 * @internal
 */
export interface DataTableV2UserActionColumnDef {
  /**
   * Allows disabling user actions on a specific column definition.
   */
  enableUserActions?: boolean;
}

//#endregion React table extension interfaces

//#region local util functions

const ACTION_FOR_ALL_COLUMNS = Symbol('Action applies to all columns');

/** Will collect the configured user actions for a specific column. */
function getCustomActionsForColumn<TData extends DataTableV2RowData>(
  columnId: string,
  optionsActions: {
    [key: string | symbol]: Array<
      | ReactNode
      | ((
          column: DataTableV2ColumnDef<TData>,
        ) => ReactElement<PropsWithChildren>)
    >;
  },
): Array<
  | ReactNode
  | ((column: DataTableV2ColumnDef<TData>) => ReactElement<PropsWithChildren>)
> {
  if (optionsActions[columnId]) {
    return (optionsActions[ACTION_FOR_ALL_COLUMNS] ?? []).concat(
      optionsActions[columnId],
    );
  }

  return optionsActions[ACTION_FOR_ALL_COLUMNS] ?? [];
}

/** Will collect the configured user actions for a specific column. */
function getCustomActionsForCell<TData extends DataTableV2RowData>(
  columnId: string,
  optionsActions: {
    [key: string | symbol]: Array<
      | ReactNode
      | ((props: {
          columnDef: DataTableV2ColumnDef<TData>;
          cellValue: unknown;
          row: TData;
          rowId: string;
        }) => ReactElement<PropsWithChildren>)
    >;
  },
): Array<
  | ReactNode
  | ((props: {
      columnDef: DataTableV2ColumnDef<TData>;
      cellValue: unknown;
      row: TData;
      rowId: string;
    }) => ReactElement<PropsWithChildren>)
> {
  if (optionsActions[columnId]) {
    return (optionsActions[ACTION_FOR_ALL_COLUMNS] ?? []).concat(
      optionsActions[columnId],
    );
  }
  return optionsActions[ACTION_FOR_ALL_COLUMNS] ?? [];
}

//#endregion local util functions

/**
 * Helper function that will let you register built in Actions
 * @internal
 */
export function registerColumnAction<TData extends RowData>(
  options: TableOptions<TData>,
  identifier: string | symbol,
  columnAction: (
    column: Column<TData>,
  ) => ReactElement<PropsWithChildren> | null,
): void {
  options.builtinColumnActions = options.builtinColumnActions ?? {};
  options.builtinColumnActions[identifier] = columnAction;
}

/** Configuration hook for the DataTableV2 UserActions feature */
export function useUserActions<TData extends DataTableV2RowData>(
  props: PropsWithChildren,
  options: TableOptions<TData>,
  consumerColumnDefMap: Map<string, DataTableV2ColumnDef<TData>>,
) {
  const configuredActions = useMemo(() => {
    const actions: {
      cellActions: {
        [key in symbol | string]: Array<
          | ReactNode
          | ((props: {
              columnDef: DataTableV2ColumnDef<TData>;
              cellValue: unknown;
              row: TData;
              rowId: string;
            }) => ReactElement<PropsWithChildren>)
        >;
      };
      builtinColumnActions: Record<
        string | symbol,
        (column: Column<TData>) => ReactElement<PropsWithChildren>
      >;
      columnActions: {
        [key in symbol | string]: Array<
          | ReactNode
          | ((
              column: DataTableV2ColumnDef<TData>,
            ) => ReactElement<PropsWithChildren>)
        >;
      };
      rowActions?: (
        row: TData,
        {
          rowDensity,
          rowIndex,
          rowId,
        }: {
          rowDensity: 'default' | 'condensed' | 'comfortable';
          rowIndex: number;
          rowId: string;
        },
      ) => ReactNode;
    } = {
      cellActions: {},
      builtinColumnActions: {},
      columnActions: {},
      rowActions: undefined,
    };

    // Iterate through the children of the DataTableV2 component in order to
    // find the `ColumnActions`, `CellActions` and `RowActions` definitions.
    Children.forEach(props.children, (child) => {
      // Check if the child is of type cell action and insert it into the actions
      // configuration.
      if (_isDataTableV2CellActions<TData>(child)) {
        // Determine if the action is targeting all columns or only a specific
        // one.
        const insertion =
          child.props.column === undefined
            ? ACTION_FOR_ALL_COLUMNS
            : child.props.column;
        // Add the collected actions into the valid actions for the column.
        actions.cellActions[insertion] = actions.cellActions[insertion] ?? [];
        actions.cellActions[insertion].push(child.props.children);
      }
      if (isDataTableV2ColumnActions<TData>(child)) {
        // Determine if the action is targeting all columns or only a specific
        // one.
        const insertion =
          child.props.column === undefined
            ? ACTION_FOR_ALL_COLUMNS
            : child.props.column;

        // Add the collected actions into the valid actions for the column.
        actions.columnActions[insertion] =
          actions.columnActions[insertion] ?? [];

        actions.columnActions[insertion].push(child.props.children);
      }
      if (isDataTableV2RowActions(child)) {
        actions.rowActions = child.props.children;
      }
    });
    return actions;
  }, [props.children]);

  options.consumerColumnDefMap = consumerColumnDefMap;
  options.enableColumnActions =
    Object.keys(configuredActions.columnActions).length > 0 ||
    configuredActions.columnActions[ACTION_FOR_ALL_COLUMNS] !== undefined;
  options.columnActions = configuredActions.columnActions;

  options.builtinColumnActions = configuredActions.builtinColumnActions;

  options.enableCellActions =
    Object.keys(configuredActions.cellActions).length > 0 ||
    configuredActions.cellActions[ACTION_FOR_ALL_COLUMNS] !== undefined;
  options.cellActions = configuredActions.cellActions;

  options.enableRowActions = configuredActions.rowActions !== undefined;
  options.rowActions = configuredActions.rowActions;
}

/**
 * Defines the user action table feature
 */
export const UserActions: TableFeature = {
  getInitialState(state?: InitialTableState) {
    return {
      ...state,
      floatingMenuInfo: {
        columnId: null,
        rowId: null,
        x: 0,
        y: 0,
      },
    };
  },

  createTable<TData extends RowData>(table: Table<TData>) {
    table._setFloatingMenuInfo = (
      columnId: string,
      rowId: string | null = null,
      x: number,
      y: number,
    ) => {
      table.setState((state) => {
        const newState = { ...state };
        newState.floatingMenuInfo = {
          type: rowId === null ? 'headerAction' : 'cellAction',
          rowId,
          columnId,
          x,
          y,
        };
        return newState;
      });
    };

    table.getUserActionsForColumn = (columnId: string) => {
      const column = table.getColumn(columnId);

      if (column?.getCanUserActions()) {
        return column.getUserActions();
      }

      return {
        builtinActions: null,
        customActions: null,
      };
    };

    table.getUserActionsForCell = (columnId: string, rowId: string) => {
      const row = table.getRow(rowId);
      const cell = row
        .getVisibleCells()
        .find((cell) => cell.column.id === columnId);

      if (cell?.getCanUserActions()) {
        return cell.getUserActions();
      }
      return {
        builtinActions: null,
        customActions: null,
      };
    };

    table.getUserActionFloatingMenuContextMenuHandler = () => {
      return (event: ReactMouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (
          event.target instanceof HTMLElement === false &&
          event.target instanceof SVGElement === false
        ) {
          return;
        }

        const cellElement = event.target.closest<HTMLElement>('[role="cell"]');
        // the click was on a body cell
        if (cellElement) {
          const { columnid } = cellElement.dataset;
          const rowid = cellElement
            .closest('[role="row"]')
            ?.getAttribute('data-rowid');

          if (!columnid || !rowid) {
            return;
          }

          // In order to check if the column or cell could take actions we get the actual cell instance.
          const row = table.getRow(rowid);
          const cell = row._getAllCellsByColumnId()[columnid];

          if (cell?.getCanUserActions()) {
            table._setFloatingMenuInfo(
              columnid,
              rowid,
              event.pageX,
              event.pageY,
            );
          }
          return;
        }

        const headerElement = event.target.closest<HTMLElement>(
          '[role="columnheader"]',
        );
        // the click happened on a header cell
        if (headerElement) {
          const { columnid } = headerElement.dataset;

          if (!columnid) {
            return;
          }
          // In order to check if the column or cell could take actions we get the actual column instance.
          const column = table.getColumn(columnid);

          if (column?.getCanUserActions()) {
            table._setFloatingMenuInfo(
              columnid,
              null,
              event.pageX,
              event.pageY,
            );
          }
        }
      };
    };
  },

  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getUserActions = memo(
      () => [
        column.id,
        table.options.columnActions,
        table.options.builtinColumnActions,
      ],
      () => {
        const customActions = getCustomActionsForColumn(
          column.id,
          table.options.columnActions ?? {},
        )
          .map((entry) => {
            // If the entry is a react element, we can directly return it
            if (isElement(entry)) {
              return entry;
            }
            // If it is a function, we will need to resolve the closure first
            if (typeof entry === 'function') {
              if (table.options.consumerColumnDefMap?.has(column.id)) {
                return entry(
                  // Need to disable non null assertion here because typescript cannot infer
                  // the non null type within a map, even if it was queried before with a has check.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  table.options.consumerColumnDefMap.get(column.id)!,
                );
              }
            }
            return null;
          })
          .filter((entry) => entry !== null);

        // Iterate over the built in actions and evaluate their result before pushing them into the
        // actions for the column.
        const builtinActions: Array<ReactNode> = [];
        for (const entry of Object.values(
          table.options.builtinColumnActions ?? {},
        )) {
          const evaluatedBuiltinAction = entry(
            column as Column<TData, unknown>,
          );
          if (evaluatedBuiltinAction !== null) {
            builtinActions.push(evaluatedBuiltinAction);
          }
        }

        return {
          builtinActions,
          customActions,
        };
      },
      getMemoOptions(table.options, 'debugHeaders', 'header.getUserActions'),
    );

    column.getCanUserActions = () => {
      if (column.columnDef.enableUserActions === false) {
        return false;
      }

      const definedHeaderActions = column.getUserActions();

      // return true only if column actions are there in column definition
      return table.options.enableColumnActions
        ? definedHeaderActions.builtinActions?.length !== 0 ||
            definedHeaderActions.customActions?.length !== 0
        : false;
    };
  },

  createCell<TData extends RowData, TValue>(
    cell: Cell<TData, TValue>,
    column: Column<TData, TValue>,
    row: Row<TData>,
    table: Table<TData>,
  ) {
    cell.getUserActions = memo(
      () => [column.id, table.options.cellActions],
      (columnId, cellActionOptions) => {
        const customActions = getCustomActionsForCell(
          columnId,
          cellActionOptions ?? {},
        ).map((entry) => {
          // If the entry is a react element, we can directly return it
          if (isElement(entry)) {
            return entry;
          }
          // If it is a function, we will need to resolve the closure first
          if (typeof entry === 'function') {
            if (table.options.consumerColumnDefMap?.has(column.id)) {
              return entry({
                // Need to disable non null assertion here because typescript cannot infer
                // the non null type within a map, even if it was queried before with a has check.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                columnDef: table.options.consumerColumnDefMap.get(column.id)!,
                cellValue: cell.getValue(),
                row: row.original,
                rowId: row.id,
              });
            }
          }
          return null;
        });

        return {
          builtinActions: null,
          customActions,
        };
      },
      getMemoOptions(table.options, 'debugCells', 'cell.getUserActions'),
    );

    cell.getCanUserActions = () => {
      if (column.columnDef.enableUserActions === false) {
        return false;
      }

      const definedCellActions = cell.getUserActions();
      return (
        definedCellActions.builtinActions?.length !== 0 &&
        definedCellActions.customActions?.length !== 0
      );
    };
  },
};
